import { useCallback } from "react";
import {
  useCancelInvite,
  useRemoveTenant
} from "../../API/queries/hostApiQueries";
import { useNotifier } from "../../context/ContextNotifier";
import { useConfirmModal } from "../../context/ContextConfirmModal";
import { IMatch } from "../../interfaces/interfaces";
import { useRejectInvite } from "../../API/queries/studentApiQueries";
import { useTranslation } from "react-i18next";

export const useHostCancelInviteWithConfirmation = () => {
  const confirmModal = useConfirmModal();
  const cancelInvite = useCancelInvite();
  const notifier = useNotifier();
  const { t } = useTranslation("chats");

  return useCallback(
    (match: IMatch) => {
      confirmModal.show({
        title: t("hooks.cancelInvite"),
        content: t("hooks.cancelInviteConfirmation"),
        onConfirm: () => {
          return new Promise((resolve) => {
            return cancelInvite.mutate(
              {
                host_id: match.host_id,
                house_id: match.house_id,
                student_id: match.student_id
              },
              {
                onSuccess: () => {
                  notifier.success({
                    message: t("hooks.cancelled")
                  });
                  resolve(null);
                }
              }
            );
          });
        },
        confirmButtonText: t("hooks.cancelInvite"),
        confirmButtonProps: { danger: true },
        cancelButtonText: t("hooks.keepInvite")
      });
    },
    [cancelInvite, confirmModal, notifier, t]
  );
};

export const useHostRemoveTenantWithConfirmation = () => {
  const confirmModal = useConfirmModal();
  const notifier = useNotifier();
  const removeTenant = useRemoveTenant();
  const { t } = useTranslation("chats");

  return useCallback(
    (match: IMatch) => {
      confirmModal.show({
        title: t("hooks.cancelPending"),
        content: t("hooks.cancelPending"),
        onConfirm: () => {
          return new Promise((resolve) => {
            removeTenant.mutate(
              {
                host_id: match.host_id,
                house_id: match.house_id,
                student_id: match.student_id
              },
              {
                onSuccess: () => {
                  notifier.success({
                    message: t("hooks.cancelled")
                  });
                  resolve(null);
                },
                onError: () => {
                  confirmModal.hide();
                }
              }
            );
          });
        },
        confirmButtonText: t("hooks.cancelInvite"),
        confirmButtonProps: { danger: true },
        cancelButtonText: t("hooks.keepInvite")
      });
    },
    [confirmModal, removeTenant, notifier, t]
  );
};

export const useStudentRejectInvite = () => {
  const confirmModal = useConfirmModal();
  const rejectInvite = useRejectInvite();
  const notifier = useNotifier();
  const { t } = useTranslation("chats");

  return useCallback(
    (match: IMatch) => {
      confirmModal.show({
        title: t("hooks.declineInvite"),
        content: t("hooks.declineInvite"),
        onConfirm: () => {
          return new Promise((resolve) => {
            rejectInvite.mutate(
              {
                host_id: match.host_id,
                house_id: match.house_id,
                student_id: match.student_id
              },
              {
                onSuccess: () => {
                  notifier.success({
                    message: t("hooks.declined")
                  });
                  resolve(null);
                },
                onError: () => {
                  confirmModal.hide();
                }
              }
            );
          });
        },
        confirmButtonText: t("hooks.declineInvite"),
        confirmButtonProps: { danger: true },
        cancelButtonText: t("hooks.keepInvite")
      });
    },
    [confirmModal, rejectInvite, notifier, t]
  );
};
